import "maplibre-gl/dist/maplibre-gl.css";
import maplibregl from "maplibre-gl/dist/maplibre-gl-csp";
// Load MapLibre's worker as a served static asset (CSP build). This avoids the
// CRA/Terser production bug where the default Blob worker (built via toString of
// minified functions) throws "o is not defined" — see MapLibre issue #1011.
// NOTE: v4 uses setWorkerUrl(), not a workerUrl property.
maplibregl.setWorkerUrl(`${process.env.PUBLIC_URL || ""}/maplibre-gl-csp-worker.js`);
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Radio, Users, MapPin, Maximize2 } from "lucide-react";
import { OSM_STYLE, toGeoJSON, makePinImage, iconName } from "./mapAssets";
import { EVENT_STATES, STATE_ORDER, stateLabel } from "@/lib/eventState";
import { locationLabel } from "@/lib/mappers";

const ROME = [12.4964, 41.9028];

export function WhooLiveMap({
  events = [],
  height = "100%",
  compact = false,
  enableGeolocate = false,
  onOpenFull,
  className = "",
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const loadedRef = useRef(false);
  const fittedRef = useRef(false);
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const [selected, setSelected] = useState(null);

  const syncData = () => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    const evs = eventsRef.current || [];
    const combos = new Map();
    evs.forEach((e) => {
      const n = iconName(e.state, e.categoryColor);
      if (!combos.has(n)) combos.set(n, [e.state, e.categoryColor]);
    });
    combos.forEach((val, name) => {
      if (!map.hasImage(name)) {
        const img = makePinImage(val[0], val[1]);
        map.addImage(name, img.image, { pixelRatio: img.pixelRatio });
      }
    });
    const src = map.getSource("events");
    if (src) src.setData(toGeoJSON(evs));
    if (evs.length && !fittedRef.current) {
      const b = new maplibregl.LngLatBounds();
      evs.forEach((e) => b.extend([e.lng, e.lat]));
      if (!b.isEmpty()) {
        map.fitBounds(b, {
          padding: compact ? 34 : 72,
          maxZoom: compact ? 11 : 13,
          duration: 600,
        });
        fittedRef.current = true;
      }
    }
  };

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: OSM_STYLE,
      center: ROME,
      zoom: compact ? 4.4 : 5.2,
    });
    mapRef.current = map;
    map.on("error", (e) => console.warn("[WhooLiveMap]", e?.error?.message || e));

    if (!compact) map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    if (enableGeolocate)
      map.addControl(
        new maplibregl.GeolocateControl({
          positionOptions: { enableHighAccuracy: true },
          trackUserLocation: true,
          showUserLocation: true,
        }),
        "top-right"
      );
    if (compact) map.scrollZoom.disable();

    map.on("load", () => {
      loadedRef.current = true;
      map.addSource("events", {
        type: "geojson",
        data: toGeoJSON([]),
        cluster: true,
        clusterRadius: 48,
        clusterMaxZoom: 14,
      });
      map.addLayer({
        id: "clusters",
        type: "circle",
        source: "events",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#2563EB",
          "circle-opacity": 0.92,
          "circle-radius": ["step", ["get", "point_count"], 16, 10, 22, 50, 30],
          "circle-stroke-width": 3,
          "circle-stroke-color": "#ffffff",
        },
      });
      map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "events",
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-font": ["Open Sans Semibold"],
          "text-size": 13,
        },
        paint: { "text-color": "#ffffff" },
      });
      map.addLayer({
        id: "points",
        type: "symbol",
        source: "events",
        filter: ["!", ["has", "point_count"]],
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": 1,
          "icon-anchor": "bottom",
          "icon-allow-overlap": true,
        },
      });

      map.on("click", "clusters", (e) => {
        const f = map.queryRenderedFeatures(e.point, { layers: ["clusters"] })[0];
        if (!f) return;
        map
          .getSource("events")
          .getClusterExpansionZoom(f.properties.cluster_id)
          .then((z) => map.easeTo({ center: f.geometry.coordinates, zoom: z }))
          .catch(() => {});
      });
      map.on("click", "points", (e) => {
        const id = e.features[0].properties.id;
        const ev = (eventsRef.current || []).find((x) => x.id === id);
        if (ev) setSelected(ev);
      });
      ["clusters", "points"].forEach((l) => {
        map.on("mouseenter", l, () => (map.getCanvas().style.cursor = "pointer"));
        map.on("mouseleave", l, () => (map.getCanvas().style.cursor = ""));
      });
      syncData();
      map.resize();
    });

    const onResize = () => map.resize();
    window.addEventListener("resize", onResize);
    const ro = new ResizeObserver(() => map.resize());
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      window.removeEventListener("resize", onResize);
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      loadedRef.current = false;
      fittedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    syncData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  return (
    <div className={`relative overflow-hidden ${className}`} style={{ height }} data-testid="whoolive-map">
      <div ref={containerRef} className="h-full w-full" />

      {/* Legend */}
      <div className="pointer-events-none absolute bottom-3 left-3 z-10 flex flex-wrap gap-2 rounded-xl bg-white/85 px-3 py-2 shadow-md backdrop-blur-md">
        {STATE_ORDER.map((s) => (
          <span key={s} className="flex items-center gap-1.5 text-[11px] font-semibold text-neutral-700">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: EVENT_STATES[s].color }} />
            {EVENT_STATES[s].label}
          </span>
        ))}
      </div>

      {onOpenFull && (
        <button
          data-testid="map-open-full"
          onClick={onOpenFull}
          className="absolute right-3 top-3 z-10 flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-2 text-xs font-semibold text-neutral-800 shadow-md backdrop-blur-md transition-colors hover:bg-white"
        >
          <Maximize2 className="h-3.5 w-3.5" /> Open full map
        </button>
      )}

      {/* Selected event card */}
      {selected && (
        <div
          data-testid="map-event-card"
          className="absolute bottom-3 left-1/2 z-20 w-[92%] max-w-md -translate-x-1/2 overflow-hidden rounded-2xl border border-black/5 bg-white shadow-2xl"
        >
          <div className="flex">
            <div
              className="h-24 w-24 shrink-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${selected.cover})` }}
            />
            <div className="min-w-0 flex-1 p-3">
              <div className="flex items-start justify-between gap-2">
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
                  style={{ backgroundColor: EVENT_STATES[selected.state]?.color }}
                >
                  {selected.state === "live" && <Radio className="h-3 w-3" />}
                  {stateLabel(selected.state)}
                </span>
                <button
                  data-testid="map-card-close"
                  onClick={() => setSelected(null)}
                  className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <h4 className="mt-1 truncate font-heading text-sm font-bold text-neutral-900">
                {selected.title}
              </h4>
              <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-neutral-500">
                <MapPin className="h-3.5 w-3.5 shrink-0" /> {locationLabel(selected.location)}
              </p>
              <div className="mt-2 flex items-center justify-between">
                <span className="flex items-center gap-1 text-xs text-neutral-500">
                  <Users className="h-3.5 w-3.5" /> {selected.viewers}
                </span>
                <Link
                  data-testid="map-card-open"
                  to={selected.liveId ? `/live/${selected.liveId}` : `/event/${selected.id}`}
                  className="rounded-full bg-brand px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-hover"
                >
                  {selected.liveId ? "Watch live" : "View event"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WhooLiveMap;
